import { useState } from 'react';
import { useAppSelector, useAppDispatch } from './store/hooks';
import { 
  selectAllOnboardings, 
  updateOnboardingFeature,
  selectFilterDsoOnboardings,
  selectSortDsoOnboardings,
  toggleFilterDsoOnboardings,
  toggleSortDsoOnboardings,
  setModalOnboarding
} from './store/onboardingSlice';
import './LivePracticeFeatures.css';

const featureStatusOptions = [
    'unknown',
    'heatlhy', 
    'recent-fixes',   
    'unhealthy',      // having issues (non-critical but legitimate, i.e. not user error)
    'broken',         // feature is essentially unusable
    'no',             // feature is not implemented
    'partial',        // feature is implemented but not to the full extent ()
    'dev'             // feature is in progress of being implemented
];

const FeatureStatus = ({onboardingId, feature, featureStatus, isOnboarding}) => {
    const dispatch = useAppDispatch();
    const [changeFeatureStatusMode, setChangeFeatureStatusMode] = useState(false);

    const handleChangeFeatureStatus = () => {
        setChangeFeatureStatusMode(!changeFeatureStatusMode);
    }

    const currentFeatureStatus = featureStatusOptions.includes(featureStatus) ? featureStatus : 'unknown';
    const cellStyle = isOnboarding ? 'feature-status-tag feature-status-grayed' : `feature-status-tag feature-status-${currentFeatureStatus}`;

    const handleSaveFeatureStatus = async (newFeatureStatus) => {
        // If the new status is different, dispatch the update
        if (newFeatureStatus !== featureStatus) {
            try {
                await dispatch(updateOnboardingFeature({
                    onboardingId,
                    feature,
                    featureStatus: newFeatureStatus
                })).unwrap();
                console.log(`Updated feature ${feature} to ${newFeatureStatus}`);
            } catch (error) {
                console.error('Failed to update feature status:', error);
            }
        }
        
        // Always call handleChangeFeatureStatus to close the dropdown
        handleChangeFeatureStatus();
    }

    if (!changeFeatureStatusMode) {
        return (
            <div className={cellStyle} onClick={isOnboarding ? undefined : handleChangeFeatureStatus} style={{cursor: isOnboarding ? 'default' : 'pointer'}}>
                {currentFeatureStatus}
            </div>
        )
    }

    return (
        <select 
            value={currentFeatureStatus} 
            onChange={(e) => handleSaveFeatureStatus(e.target.value)}
            className="feature-status-dropdown"
            autoFocus
        >
            {featureStatusOptions.map(option => (
                <option key={option} value={option}>
                    {option}
                </option>
            ))}
        </select>
    );
}


const LivePracticeFeatures = () => {
    const dispatch = useAppDispatch();
    const onboardings = useAppSelector(selectAllOnboardings);
    const settingFilterDsoOnboardings = useAppSelector(selectFilterDsoOnboardings);
    const settingSortDsoOnboardings = useAppSelector(selectSortDsoOnboardings);

    const sortBy = 'createdAt';
    
    // State for column visibility
    const [hiddenColumns, setHiddenColumns] = useState(new Set());

    // Filter to only show live practices (not onboarding)
    // const livePractices = onboardings.filter((onboarding) => {
    //     return !onboarding.bIsOnboarding;
    // });

    const livePractices = onboardings;
    // Apply DSO filtering
    const filteredPractices = livePractices.filter((onboarding) => {
        if (settingFilterDsoOnboardings) {
            return onboarding.isDso;
        }
        return true;
    });

    // Apply sorting and grouping
    const getGroupSortedPractices = (practices) => {
        const groups_map = practices.reduce((acc, onboarding) => {
            if (acc[onboarding.organizationId]) {
                acc[onboarding.organizationId].push(onboarding);
            } else {
                acc[onboarding.organizationId] = [onboarding];
            }
            return acc;
        }, {});

        const sortedInGroups = Object.values(groups_map).map(group => {
            return group.sort((a, b) => {
                return a[sortBy] - b[sortBy];
            });
        });

        const sortedGroups = sortedInGroups.sort((a, b) => {
            return a[0][sortBy] - b[0][sortBy];
        });
        
        return sortedGroups.flat();
    };

    const sortedPractices = getGroupSortedPractices(filteredPractices);

    // Group practices for outline styling when settingSortDsoOnboardings is true
    const getGroupedPractices = (practices) => {
        if (!settingSortDsoOnboardings) {
            return practices.map(practice => ({ ...practice, groupStart: false, groupEnd: false }));
        }

        const groups = {};
        practices.forEach(practice => {
            if (!groups[practice.organizationId]) {
                groups[practice.organizationId] = [];
            }
            groups[practice.organizationId].push(practice);
        });

        const result = [];
        Object.values(groups).forEach(group => {
            // Only apply grouping to DSO practices
            if (group[0]?.isDso) {
                result.push(...group.map((practice, index) => ({
                    ...practice,
                    groupStart: index === 0,
                    groupEnd: index === group.length - 1
                })));
            } else {
                // Non-DSO practices don't get grouping
                result.push(...group.map(practice => ({ ...practice, groupStart: false, groupEnd: false })));
            }
        });

        return result;
    };

    const groupedPractices = getGroupedPractices(sortedPractices);

    const onboardingFeatures = groupedPractices.map(onboarding =>
        ({
            id: onboarding.id, 
            practiceId: onboarding.practiceId,
            name: onboarding.name,
            productType: onboarding.productType,
            features: onboarding?.features || {},
            bIsOnboarding: onboarding.bIsOnboarding,
            isDso: onboarding.isDso,
            groupStart: onboarding.groupStart,
            groupEnd: onboarding.groupEnd
        })
    );

    const featureSet = ['Scheduling', 'Recall', 'Confirmations', 'Reviews', 'Messaging', 'Emergency', 'Fax']

    const handleLocationClick = (onboardingId) => {
        dispatch(setModalOnboarding({ onboardingId, stage: 'overview' }));
    };

    const handlePracticeIdClick = (practiceId) => {
        navigator.clipboard.writeText(practiceId).then(() => {
            // Optional: You could add a toast notification here
            console.log('Practice ID copied to clipboard:', practiceId);
        }).catch(err => {
            console.error('Failed to copy practice ID:', err);
        });
    };

    const handleMinimizeColumn = (feature) => {
        setHiddenColumns(prev => new Set([...prev, feature]));
    };

    const handleRestoreColumns = () => {
        setHiddenColumns(new Set());
    };

    return (
        <>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <h1>Live Practice Features ({onboardingFeatures.length})</h1>
            <div style={{ display: 'flex', gap: '12px' }}>
                <button 
                    onClick={() => dispatch(toggleFilterDsoOnboardings())}
                    style={{
                        padding: '8px 16px',
                        backgroundColor: settingFilterDsoOnboardings ? '#007bff' : '#f8f9fa',
                        color: settingFilterDsoOnboardings ? 'white' : '#333',
                        border: '1px solid #dee2e6',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '14px'
                    }}
                >
                    Filter DSO Only
                </button>
                <button 
                    onClick={() => dispatch(toggleSortDsoOnboardings())}
                    style={{
                        padding: '8px 16px',
                        backgroundColor: settingSortDsoOnboardings ? '#007bff' : '#f8f9fa',
                        color: settingSortDsoOnboardings ? 'white' : '#333',
                        border: '1px solid #dee2e6',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '14px'
                    }}
                >
                    Group by Organization
                </button>
                <button 
                    onClick={handleRestoreColumns}
                    style={{
                        padding: '8px 16px',
                        backgroundColor: hiddenColumns.size > 0 ? '#28a745' : '#f8f9fa',
                        color: hiddenColumns.size > 0 ? 'white' : '#333',
                        border: '1px solid #dee2e6',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '14px'
                    }}
                >
                    Restore Columns
                </button>
            </div>
        </div>
        <div>
            <table>
                <thead>
                    <tr>
                        <th>Location</th>
                        <th>Practice ID</th>
                        <th>Product Type</th>
                        {featureSet.map(feature => (
                            !hiddenColumns.has(feature) && (
                            <th key={feature} style={{ position: 'relative' }}>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                    <span>{feature}</span>
                                    <button
                                        onClick={() => handleMinimizeColumn(feature)}
                                        style={{
                                            background: 'none',
                                            border: 'none',
                                            color: '#dc3545',
                                            cursor: 'pointer',
                                            fontSize: '12px',
                                            padding: '2px 4px',
                                            marginLeft: '8px'
                                        }}
                                        title="Minimize column"
                                    >
                                        ×
                                    </button>
                                </div>
                            </th>
                            )
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {onboardingFeatures.map(onboarding => {
                        // Determine CSS classes for grouping outline
                        let rowClasses = onboarding.bIsOnboarding ? 'onboarding-row' : '';
                        if (onboarding.isDso && settingSortDsoOnboardings) {
                            if (onboarding.groupStart && onboarding.groupEnd) {
                                // Single row group
                                rowClasses += ' dso-group-single';
                            } 
                            if (onboarding.groupStart) {
                                // First row of group
                                rowClasses += ' dso-group-start';
                            } 
                            if (onboarding.groupEnd) {
                                // Last row of group
                                rowClasses += ' dso-group-end';
                            } 
                            
                            // Middle row of group
                            rowClasses += ' dso-group-middle';
                            
                        }

                        return (
                            <tr key={onboarding.id} className={rowClasses}>
                                <td 
                                    onClick={() => handleLocationClick(onboarding.id)}
                                    style={{ cursor: 'pointer', color: '#007bff' }}
                                >
                                    {onboarding.name}
                                </td>
                                <td 
                                    onClick={() => handlePracticeIdClick(onboarding.practiceId)}
                                    style={{ cursor: 'pointer', color: '#007bff' }}
                                    title="Click to copy Practice ID"
                                >
                                    {onboarding.practiceId}
                                </td>
                                <td>{onboarding?.productType || ''}</td>
                                {featureSet.map(feature => (
                                    !hiddenColumns.has(feature) && (
                                        <td key={feature}>
                                            <FeatureStatus 
                                                onboardingId={onboarding.id} 
                                                feature={feature} 
                                                featureStatus={onboarding?.features?.[feature] || ''} 
                                                isOnboarding={onboarding.bIsOnboarding} 
                                            />
                                        </td>
                                    )
                                ))}
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
        </>
    );
};

export { LivePracticeFeatures };